<div class="container-fluid leave-fixed-viewport">
  <div class="card leave-dashboard-card">

    <!-- Card Header -->
    <div class="card-header">
      <div class="row align-items-center">
        <div class="col-md-6">
          <div class="header-title">
            <h2 class="mb-0">{{ isCreateMode ? 'Create Leave Request' : 'Edit Leave Request' }}</h2>
            <p class="subtitle mb-0">{{ isCreateMode ? 'Fill in the details to submit a new leave application' : 'Review
              and update leave application details' }}</p>
          </div>
        </div>
        <div class="col-md-6 d-flex justify-content-end align-items-center gap-2">
          <button type="button" class="btn-header-action" routerLink="/admin/Leave-request-view">
            <i class="fas fa-arrow-left me-1"></i> Back to List
          </button>
          <button type="submit" form="leaveEditForm" class="btn-save" [disabled]="submitting">
            <span *ngIf="!submitting">
              <i class="fas fa-save me-1"></i> {{ isCreateMode ? 'Submit Request' : 'Save Changes' }}
            </span>
            <span *ngIf="submitting">
              <i class="fas fa-spinner fa-spin me-1"></i> {{ isCreateMode ? 'Submitting...' : 'Saving...' }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div *ngIf="loading" class="leave-loading-container">
      <div class="leave-custom-loader">
        <div class="leave-loader-emblem-container">
          <img src="assets/img/logo.png" alt="Company Logo" class="leave-loader-emblem" />
          <div class="leave-loading-circle"></div>
        </div>
        <p class="leave-loading-text">{{ isCreateMode ? 'Preparing form...' : 'Loading leave request...' }}</p>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body leave-edit-body" *ngIf="!loading">
      <form id="leaveEditForm" [formGroup]="creationForm" (ngSubmit)="submit()">

        <!-- Status Banner (edit mode only) -->
        <div class="status-banner" [ngClass]="getStatusBannerClass(creationForm.get('status')?.value)"
          *ngIf="!isCreateMode && creationForm.get('status')?.value">
          <i class="fas" [ngClass]="getStatusIcon(creationForm.get('status')?.value)"></i>
          <span>This request is currently <strong>{{ creationForm.get('status')?.value }}</strong></span>
        </div>

        <div class="edit-grid">

          <!-- ── LEFT PANEL: Employee & Dates ── -->
          <div class="edit-panel">
            <div class="panel-heading">
              <i class="fas fa-user-circle panel-icon"></i>
              <span>Employee & Leave Details</span>
            </div>

            <!-- Employee (hidden in create mode, auto-filled in background) -->
            <div class="field-group" *ngIf="!isCreateMode">
              <label class="field-label">Employee</label>
              <div class="readonly-field">
                <i class="fas fa-user readonly-icon"></i>
                <input readonly class="form-control edit-input" type="text" formControlName="employee_name" />
              </div>
            </div>

            <!-- Leave Type -->
            <div class="field-group">
              <label class="field-label required-label">Leave Type</label>
              <div class="select-wrapper">
                <select class="form-control edit-select" formControlName="leave_type">
                  <option value="">-- Select Type --</option>
                  <option *ngFor="let item of leaveTypes" [value]="item">{{ item }}</option>
                </select>
                <i class="fas fa-chevron-down select-arrow"></i>
              </div>
              <div *ngIf="creationForm.get('leave_type')?.invalid && creationForm.get('leave_type')?.touched"
                class="field-error">
                <i class="fas fa-exclamation-circle me-1"></i> Leave Type is required
              </div>
            </div>

            <!-- Marriage Leave Toggle -->
            <div class="field-group" *ngIf="creationForm.get('leave_type')?.value === 'Vacation Leave'">
              <label class="toggle-label">
                <input type="checkbox" formControlName="is_marriage_leave" class="toggle-check" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">
                  <i class="fas fa-ring me-1"></i> This is a Marriage Leave
                </span>
              </label>
            </div>

            <!-- From Date -->
            <div class="field-group">
              <label class="field-label required-label">From Date</label>
              <input class="form-control edit-input" type="date" formControlName="from_date" />
              <div *ngIf="creationForm.get('from_date')?.invalid && creationForm.get('from_date')?.touched"
                class="field-error">
                <i class="fas fa-exclamation-circle me-1"></i> From Date is required
              </div>
            </div>

            <!-- To Date -->
            <div class="field-group">
              <label class="field-label required-label">To Date</label>
              <input class="form-control edit-input" type="date" formControlName="to_date" />
              <div *ngIf="creationForm.get('to_date')?.invalid && creationForm.get('to_date')?.touched"
                class="field-error">
                <i class="fas fa-exclamation-circle me-1"></i> To Date is required
              </div>
            </div>

            <!-- Duration -->
            <div class="field-group">
              <label class="field-label">Duration</label>
              <div class="duration-box">
                <i class="fas fa-sun"></i>
                <span>{{ getDaysPreview() }}</span>
              </div>
            </div>

            <!-- Document Upload (create mode only) -->
            <div class="field-group" *ngIf="isCreateMode">
              <label class="field-label">Supporting Document</label>
              <input class="form-control edit-input" type="file" (change)="onFileChange($event)"
                accept=".pdf,.jpg,.jpeg,.png" />
              <small class="text-muted">Optional — PDF, JPG, PNG accepted</small>
            </div>

          </div>

          <!-- ── RIGHT PANEL: Status & Reason ── -->
          <div class="edit-panel">
            <div class="panel-heading">
              <i class="fas fa-clipboard-list panel-icon"></i>
              <span>{{ isCreateMode ? 'Leave Reason' : 'Status & Remarks' }}</span>
            </div>

            <!-- Status (edit mode only) -->
            <div class="field-group" *ngIf="!isCreateMode">
              <label class="field-label required-label">Status</label>
              <div class="select-wrapper">
                <select class="form-control edit-select" formControlName="status">
                  <option value="">-- Select Status --</option>
                  <option *ngFor="let item of statusList" [value]="item">{{ item }}</option>
                </select>
                <i class="fas fa-chevron-down select-arrow"></i>
              </div>
              <div *ngIf="creationForm.get('status')?.invalid && creationForm.get('status')?.touched"
                class="field-error">
                <i class="fas fa-exclamation-circle me-1"></i> Status is required
              </div>
            </div>

            <!-- Reason for Leave -->
            <div class="field-group">
              <label class="field-label" [class.required-label]="isCreateMode">Reason for Leave</label>
              <textarea class="form-control edit-textarea" [class.reason-readonly]="!isCreateMode"
                formControlName="reason" rows="4"
                [placeholder]="isCreateMode ? 'Enter your reason for leave...' : 'No reason provided'"
                [attr.readonly]="!isCreateMode ? true : null">
              </textarea>
              <div *ngIf="isCreateMode && creationForm.get('reason')?.invalid && creationForm.get('reason')?.touched"
                class="field-error">
                <i class="fas fa-exclamation-circle me-1"></i> Reason is required
              </div>
            </div>

            <!-- Rejection Reason (edit mode, conditional) -->
            <div class="field-group" *ngIf="!isCreateMode && creationForm.get('status')?.value === 'Rejected'">
              <label class="field-label required-label danger-label">
                <i class="fas fa-ban me-1"></i> Rejection Reason
              </label>
              <textarea class="form-control edit-textarea textarea-danger" formControlName="reject_reason" rows="3"
                placeholder="Enter rejection reason..."></textarea>
              <div *ngIf="creationForm.get('reject_reason')?.invalid && creationForm.get('reject_reason')?.touched"
                class="field-error">
                <i class="fas fa-exclamation-circle me-1"></i> Rejection Reason is required
              </div>
            </div>

            <!-- Under Verification Reason (edit mode, conditional) -->
            <div class="field-group"
              *ngIf="!isCreateMode && creationForm.get('status')?.value === 'Under Verification'">
              <label class="field-label warning-label">
                <i class="fas fa-search me-1"></i> Decline / Review Reason
              </label>
              <textarea class="form-control edit-textarea textarea-warning" formControlName="decline_reason" rows="3"
                placeholder="Enter reason for verification..."></textarea>
            </div>

            <!-- Create mode info box -->
            <div class="info-box" *ngIf="isCreateMode">
              <i class="fas fa-info-circle me-2"></i>
              <span>The request will be sent to HR for review. You will be notified once it is processed.</span>
            </div>

          </div>

        </div>
      </form>
    </div>

  </div>
</div>